import { useEffect, useState } from "react";
import { cacheImage, getCachedImage } from "@/lib/idb-images";

interface CachedImageProps {
  src: string;
  alt?: string;
  className?: string;
  chatId?: string;
  onClick?: (e: React.MouseEvent) => void;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

export function CachedImage({
  src,
  alt,
  className,
  chatId,
  onClick,
  onError,
}: CachedImageProps) {
  const [displaySrc, setDisplaySrc] = useState<string>(src);
  const [isLoading, setIsLoading] = useState(true);
  const [isCached, setIsCached] = useState(false);

  useEffect(() => {
    let objectUrl: string | null = null;

    const loadImage = async () => {
      try {
        // 1. 캐시된 이미지 확인
        const cached = await getCachedImage(src);

        if (cached) {
          objectUrl = cached;
          setDisplaySrc(cached);
          setIsCached(true);
        } else {
          // 2. 캐시에 없으면 원본 사용 (캐싱은 onLoad에서 수행)
          setDisplaySrc(src);
          setIsCached(false);
        }
      } catch (error) {
        console.error("Failed to load image:", error);
        setDisplaySrc(src);
      } finally {
        setIsLoading(false);
      }
    };

    loadImage();

    // Cleanup: Object URL 메모리 해제
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src, chatId]);

  // 이미지 로딩 성공 시에만 캐싱
  const handleImageLoad = () => {
    setIsLoading(false);

    // 이미 캐시된 이미지가 아닌 경우에만 캐싱
    if (!isCached) {
      cacheImage(src, chatId); // 비동기로 캐싱
    }
  };

  return (
    <img
      src={displaySrc}
      alt={alt}
      className={className}
      loading="lazy"
      style={{ opacity: isLoading ? 0.5 : 1 }}
      onClick={onClick}
      onLoad={handleImageLoad}
      onError={onError}
    />
  );
}
